import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import MenuPage2 from './MenuPage2';
import { loadMenuPages } from '../services/menu-pages';

export default function LandingPage() {
  const navigate = useNavigate();
  const reducedMotion = Boolean(useReducedMotion());
  const hostRef = useRef(null);
  const sceneRef = useRef(null);
  const timerRef = useRef(null);
  const enteringRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [entering, setEntering] = useState(false);
  const [failed, setFailed] = useState(false);
  const [arrived, setArrived] = useState(false);
  const [menuReady, setMenuReady] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const markMenuReady = useCallback(() => setMenuReady(true), []);

  // Keep the 3D scene alive underneath a decoded menu page during the handoff.
  useEffect(() => {
    if (!arrived) return;
    let timer;
    if (menuReady) {
      setRevealing(true);
      timer = window.setTimeout(() => navigate('/menu', { state: { fromCafe: true } }), reducedMotion ? 150 : 900);
    } else timer = window.setTimeout(() => setMenuReady(true), 2200);
    return () => window.clearTimeout(timer);
  }, [arrived, menuReady, navigate, reducedMotion]);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    const fail = () => {
      if (cancelled) return;
      setReady(false); setFailed(true);
      if (enteringRef.current) {
        window.clearTimeout(timerRef.current);
        setArrived(true);
      }
    };
    import('../components/createCafeScene').then(({ createCafeScene }) => {
      if (cancelled) return;
      sceneRef.current = createCafeScene(hostRef.current, {
        reducedMotion: Boolean(reducedMotion),
        onReady: () => { if (!cancelled) { setReady(true); setFailed(false); } },
        onError: fail,
      });
    }).catch(fail);
    return () => { cancelled = true; sceneRef.current?.dispose(); sceneRef.current = null; };
  }, [reducedMotion, navigate]);

  useEffect(() => {
    let active = true;
    loadMenuPages().then(pages => {
      if (active && pages[0]) new Image().src = pages[0].image_url;
    }).catch(() => {});
    return () => { active = false; window.clearTimeout(timerRef.current); };
  }, []);

  const enterCafe = () => {
    if (enteringRef.current) return;
    enteringRef.current = true; setEntering(true);
    const complete = () => { window.clearTimeout(timerRef.current); setArrived(true); };
    if (!reducedMotion && ready && sceneRef.current?.enter(complete)) {
      timerRef.current = window.setTimeout(complete, 9500);
    } else timerRef.current = window.setTimeout(complete, 180);
  };

  return (
    <main className={`cafe-entry${ready ? ' cafe-entry--ready' : ''}${entering ? ' cafe-entry--entering' : ''}`} aria-label="Kukas Cafe" aria-busy={entering}>
      <picture className="cafe-entry__fallback">
        <source media="(max-aspect-ratio: 85/100)" srcSet="/images/cafe-preview-mobile.webp" />
        <img src="/images/cafe-preview-desktop.webp" alt="" fetchPriority="high" />
      </picture>
      <div className="cafe-entry__canvas" ref={hostRef} />
      <div className="cafe-entry__shade" aria-hidden="true" />
      <header className="cafe-entry__brand" aria-label="Kukas Cafe"><span>kukas</span><small>cafe</small></header>
      <div className="cafe-entry__controls">
        <button className="cafe-entry__enter" onClick={enterCafe} disabled={entering}>
          <span>Menüyü Gör</span>
          <svg width="23" height="20" viewBox="0 0 24 20" fill="none" aria-hidden="true"><path d="M3 10h17M14 4l6 6-6 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        {!ready && !failed && <span className="cafe-entry__loading" role="status" aria-label="Yükleniyor"><i /><i /><i /></span>}
      </div>
      {entering && <div className={`cafe-entry__menu-reveal${revealing ? ' cafe-entry__menu-reveal--visible' : ''}`} inert aria-hidden="true">
        <MenuPage2 embedded onReady={markMenuReady} />
      </div>}
    </main>
  );
}
